/**
 *  NKP-EREDMÉNYEK BEOLVASÁSA – a Nemzeti Köznevelési Portál „Legjobb eredmény" listájából.
 *
 *  A tanár a böngészőben kijelöli és bemásolja a listát. Egy sor alakja (tabulátorral):
 *
 *    Koltai Ábel abel.koltai@gmail.com	Nem kezdett hozzá a feladathoz!	 	Megjegyzések
 *    krupp noemi kruppnoemi@gmail.com	48.48 pont (48.5%)	1 megoldás 	Megjegyzések
 *
 *  - a név után a tanuló (magán)e-mail címe áll – a névsorunkban többnyire nincs meg, ezért
 *    a NÉV a fő párosítási kulcs, az e-mail csak ha egyezik;
 *  - az eredmény kétszer szerepel: pont és (kerekített) százalék – a zárójeles százalék kell;
 *  - két szöveges állapot: „Nem kezdett hozzá a feladathoz!" (hiányzott) és „Még nem adta be"
 *    (elkezdte, de nem adta be időben) – mindkettő „nem írta meg";
 *  - az időkedvezményesek külön listában jönnek: a két listát egymás után, akár üres sorral
 *    elválasztva lehet bemásolni; a fejléc-sorokat kihagyjuk.
 */

export type NkpAllapot = 'eredmeny' | 'nem_kezdett' | 'nem_adta_be';

export interface NkpSor {
  sor: number;              // a beillesztett szöveg sora (1-től), a visszajelzéshez
  nev: string;
  email: string | null;
  allapot: NkpAllapot;
  pont: number | null;
  szazalek: number | null;  // a kerekített (zárójeles) százalék
  nyers: string;
}

const EMAIL = /[^\s<>()"']+@[^\s<>()"']+\.[a-z]{2,}/i;
const FEJLEC = /^felhaszn[aá]l[oó]\s/i;

const szam = (s: string): number | null => {
  const n = Number(String(s).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** A beillesztett NKP-lista soronként. A nem értelmezhető sorokat `hibak`-ban adja vissza. */
export function nkpBeolvas(szoveg: string): { sorok: NkpSor[]; hibak: { sor: number; szoveg: string }[] } {
  const sorok: NkpSor[] = [];
  const hibak: { sor: number; szoveg: string }[] = [];
  const lista = String(szoveg || '').replace(/\r\n?/g, '\n').split('\n');
  lista.forEach((nyers, i) => {
    const s = nyers.replace(/ /g, ' ').trim();
    if (!s || FEJLEC.test(s)) return;
    const email = (s.match(EMAIL) || [])[0] || null;
    //  A név az e-mail előtti rész (tabulátor nélküli másolásnál is), különben az első oszlop.
    const elso = s.split('\t')[0];
    let nev = email ? s.slice(0, s.indexOf(email)) : elso;
    nev = nev.replace(/\t/g, ' ').replace(/\s+/g, ' ').trim();
    const maradek = email ? s.slice(s.indexOf(email) + email.length) : s.slice(elso.length);

    let allapot: NkpAllapot | null = null;
    let pont: number | null = null;
    let szazalek: number | null = null;
    const m = maradek.match(/(-?[\d.,]+)\s*pont\s*\(\s*(-?[\d.,]+)\s*%\s*\)/i);
    if (m) {
      allapot = 'eredmeny';
      pont = szam(m[1]);
      szazalek = szam(m[2]);
    } else if (/nem\s+kezdett\s+hozz/i.test(maradek)) {
      allapot = 'nem_kezdett';
    } else if (/m[ée]g\s+nem\s+adta\s+be/i.test(maradek)) {
      allapot = 'nem_adta_be';
    } else {
      //  Csak százalék, pont nélkül (ha az NKP egyszer így adná).
      const p = maradek.match(/(-?[\d.,]+)\s*%/);
      if (p) { allapot = 'eredmeny'; szazalek = szam(p[1]); }
    }
    if (!nev || !allapot || (allapot === 'eredmeny' && szazalek === null)) {
      hibak.push({ sor: i + 1, szoveg: s.slice(0, 120) });
      return;
    }
    sorok.push({ sor: i + 1, nev, email: email ? email.toLowerCase() : null, allapot, pont, szazalek, nyers: s });
  });
  return { sorok, hibak };
}

/* ── Párosítás a névsorhoz ────────────────────────────────────────────────── */

/** Kisbetű, ékezet és írásjel nélkül, a szavak ábécérendben – a sorrend (vezeték/kereszt) nem számít. */
export function nevKulcs(nev: string): string {
  return String(nev || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9\s-]/g, ' ')
    .split(/[\s-]+/).filter((x) => x.length > 1)      // az „Z." típusú kezdőbetűt elhagyjuk
    .sort().join(' ');
}

export interface NevsorTag { oktazon: string; nev: string; email?: string | null; }

/**
 *  Melyik tanuló? Sorrend: e-mail egyezés → teljes név (szórendtől függetlenül) → a rövidebb
 *  név minden szava megvan a hosszabbikban (pl. „Wendland Bernhard Botond" ↔ „Wendland Bernhard").
 *  Csak EGYÉRTELMŰ találatot adunk vissza; több jelöltnél null – azt a tanár választja ki.
 */
export function nkpParosit(sor: NkpSor, nevsor: NevsorTag[]): { tag: NevsorTag | null; mod: 'email' | 'nev' | 'reszleges' | null; jeloltek: NevsorTag[] } {
  if (sor.email) {
    const e = nevsor.filter((t) => t.email && String(t.email).toLowerCase() === sor.email);
    if (e.length === 1) return { tag: e[0], mod: 'email', jeloltek: e };
  }
  const k = nevKulcs(sor.nev);
  const pontos = nevsor.filter((t) => nevKulcs(t.nev) === k);
  if (pontos.length === 1) return { tag: pontos[0], mod: 'nev', jeloltek: pontos };
  if (pontos.length > 1) return { tag: null, mod: null, jeloltek: pontos };
  const szavak = k.split(' ').filter(Boolean);
  const resz = nevsor.filter((t) => {
    const tk = nevKulcs(t.nev).split(' ');
    const [rovid, hosszu] = szavak.length <= tk.length ? [szavak, tk] : [tk, szavak];
    return rovid.length >= 2 && rovid.every((w) => hosszu.includes(w));
  });
  if (resz.length === 1) return { tag: resz[0], mod: 'reszleges', jeloltek: resz };
  return { tag: null, mod: null, jeloltek: resz };
}

/** Ponthatárok szerinti érdemjegy: `hatarok` = a 2-es, 3-as, 4-es, 5-ös ALSÓ határa százalékban. */
export function jegyHatarral(szazalek: number, hatarok: [number, number, number, number]): number {
  const [h2, h3, h4, h5] = hatarok;
  if (szazalek >= h5) return 5;
  if (szazalek >= h4) return 4;
  if (szazalek >= h3) return 3;
  if (szazalek >= h2) return 2;
  return 1;
}
